import type { CaptureResult } from 'posthog-js'

// Imported from the module rather than the `src/router` barrel on purpose: the
// barrel re-exports `./routes`, which imports every page in the app. This file
// runs at boot, before React mounts, and must not drag the route tree with it.
import { paths } from 'src/router/paths'

/**
 * Property keys that must never leave the browser (BR-44: pseudonymous
 * identifiers only). Stripped from every event regardless of who set them —
 * `property_denylist` covers the top level, this covers ours as well as
 * anything the SDK derives.
 */
const PII_KEYS = [
  'email',
  'emails',
  'name',
  'first_name',
  'last_name',
  'full_name',
  'company_name',
  'workspace_name',
  'domain_name',
  'handle',
  'phone'
] as const

/**
 * URL properties whose query string is dropped.
 *
 * Invitation and one-time-login links carry their token in the query, and an
 * event that records the URL verbatim turns the analytics store into a place
 * credentials can be read out of.
 */
const URL_KEYS = ['$current_url', '$referrer', '$initial_current_url'] as const

/**
 * Route prefixes `$pageview` is kept for.
 *
 * Everything else is dropped. Pageviews are needed for the funnel's first leg
 * (landing → signup) and nowhere else in this iteration, and account-ui is a
 * multi-page admin app — capturing all of it would blow the onboarding event
 * budget on pages no insight reads. Widening this list is a deliberate act with
 * a cost, not a default.
 */
const PAGEVIEW_PREFIXES = [
  paths.auth.login,
  paths.auth.register,
  paths.auth.confirmRegister,
  paths.auth.emailOtpVerify,
  paths.auth.emailOtpLink,
  paths.auth.invitation,
  paths.workspaces.new
] as const

const stripQuery = (value: unknown): unknown => {
  if (typeof value !== 'string') return value

  const cut = value.search(/[?#]/)

  return cut === -1 ? value : value.slice(0, cut)
}

const isInScope = (pathname: string): boolean =>
  PAGEVIEW_PREFIXES.some((prefix) => pathname.startsWith(prefix))

/**
 * Reads the path out of whatever `$pathname`/`$current_url` the SDK produced.
 * Falls back to the live location: a pageview with neither is not a pageview we
 * can place, and dropping it is safer than keeping an unattributable one.
 */
const pathnameOf = (properties: Record<string, unknown>): string | null => {
  const pathname = properties.$pathname
  if (typeof pathname === 'string') return pathname

  const url = properties.$current_url
  if (typeof url === 'string') {
    try {
      return new URL(url).pathname
    } catch {
      return null
    }
  }

  return null
}

/**
 * `before_send` hook. Runs on every event, ours and the SDK's own.
 *
 * Three jobs, in order: drop out-of-scope pageviews, strip PII, strip URL query
 * strings. Returning `null` discards the event.
 */
export const sanitize = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return null

  const properties = event.properties as Record<string, unknown> | undefined
  if (!properties) return event

  if (event.event === '$pageview' || event.event === '$pageleave') {
    const pathname = pathnameOf(properties)
    if (pathname === null || !isInScope(pathname)) return null
  }

  for (const key of PII_KEYS) {
    if (key in properties) delete properties[key]
  }

  for (const key of URL_KEYS) {
    if (key in properties) properties[key] = stripQuery(properties[key])
  }

  return event
}
